import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Checkbox, Typography, Spin, message, Select, Divider, Space } from "antd";
import { PlusOutlined, DeleteOutlined, SaveOutlined, SecurityScanOutlined, ProfileOutlined } from "@ant-design/icons";
import userApi from "../../services/api/userApi";
import useAppSession from "../../stores/useAppSession";
import { useIntl } from "react-intl";
import useChat from "../../stores/useChat";
import { PageWrapper, ProfileCard, StyledForm, DynamicSection } from "./UserProfile";

const { Title, Text } = Typography;

const UserProfile = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { token, clientId, clearSession } = useAppSession();
  const chat = useChat();

  const [userData, setUserData] = useState({
    userid: "", login: "", name: "", noexpiration: false,
    template_saudacao: "", template_contexto: "", template_internet: "",
    memory_saudacao: "", memory_despedida: "",
    finalStepQuestions: [], rulesConditions: []
  });

  const [loading, setLoading] = useState(false);

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const userRes = await userApi.getCurrentUser(clientId);
      if (userRes.status === 401) { clearSession(); return; }

      const user = userRes.data.user;
      const tmplRes = await userApi.getTemplateContext(user.userid);
      const tmpl = tmplRes.data;

      setUserData({
        userid: user.userid,
        login: user.login,
        name: user.name,
        noexpiration: user.noexpiration,
        template_saudacao: tmpl.context.find((t) => t.template_name === "template_saudacao")?.content || "",
        template_contexto: tmpl.context.find((t) => t.template_name === "template_contexto")?.content || "",
        template_internet: tmpl.context.find((t) => t.template_name === "template_internet")?.content || "",
        memory_saudacao: chat.memory_saudacao || "",
        memory_despedida: chat.memory_despedida || "",
        finalStepQuestions: chat.finalStepQuestions || [],
        rulesConditions: chat.rulesConditions || [],
      });
    } catch (error) {
      message.error("Erro ao carregar dados.");
    } finally {
      setLoading(false);
    }
  }, [clientId, chat]);

  useEffect(() => { if (clientId) fetchUserData(); }, [clientId, fetchUserData]);

  const handleSave = async () => {
    try {
      setLoading(true);
      await userApi.updateProfile(userData.userid, { nome: userData.name });
      await userApi.updateTemplateContext({
        userid: userData.userid,
        template_saudacao: userData.template_saudacao,
        template_contexto: userData.template_contexto,
        template_internet: userData.template_internet,
      });

      chat.setRulesConditions(userData.rulesConditions);
      chat.setFinalStepQuestions(userData.finalStepQuestions);
      chat.setMemorySaudacao(userData.memory_saudacao);
      chat.setMemoryDespedida(userData.memory_despedida);

      message.success("Perfil atualizado com sucesso.");
    } catch (error) {
      message.error("Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  const makeUid = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <PageWrapper>
      <ProfileCard>
        <header style={{ marginBottom: 32 }}>
          <Title level={2} style={{ margin: 0, letterSpacing: '-0.5px' }}>Perfil do Usuário</Title>
          <Text type="secondary">Gerencie suas credenciais e configurações de comportamento da IA.</Text>
        </header>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}><Spin /></div>
        ) : (
          <StyledForm>
            <Form layout="vertical" onFinish={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <Form.Item label="Identificador (ID)">
                  <Input value={userData.userid} disabled />
                </Form.Item>
                <Form.Item label="Login de Acesso">
                  <Input value={userData.login} disabled />
                </Form.Item>

              </div>

              <Form.Item label="Nome Completo">
                <Input
                  value={userData.name}
                  onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                />
              </Form.Item>

              <Form.Item>
                <Checkbox
                  checked={userData.noexpiration}
                  onChange={(e) => setUserData({ ...userData, noexpiration: e.target.checked })}
                >
                  Esta conta não expira automaticamente
                </Checkbox>
              </Form.Item>

              <Divider orientation="center">Configurações de Prompt & Templates</Divider>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <Form.Item label="Template de Saudação">
                  <Input.TextArea rows={4} value={userData.template_saudacao} onChange={(e) => setUserData({ ...userData, template_saudacao: e.target.value })} />
                </Form.Item>
                <Form.Item label="Memória de Saudação">
                  <Input.TextArea rows={4} value={userData.memory_saudacao} onChange={(e) => setUserData({ ...userData, memory_saudacao: e.target.value })} />
                </Form.Item>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <Form.Item label="Template de Contexto">
                  <Input.TextArea rows={4} value={userData.template_contexto} onChange={(e) => setUserData({ ...userData, template_contexto: e.target.value })} />
                </Form.Item>
                <Form.Item label="Template de Internet">
                  <Input.TextArea rows={4} value={userData.template_internet} onChange={(e) => setUserData({ ...userData, template_internet: e.target.value })} />
                </Form.Item>
              </div>

              <Form.Item label="Template de Despedida">
                <Input.TextArea rows={4} value={userData.memory_despedida} onChange={(e) => setUserData({ ...userData, memory_despedida: e.target.value })} />
              </Form.Item>


              <DynamicSection>
                <Title level={4}><ProfileOutlined /> Questionário Dinâmico</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Defina os campos que a IA deve coletar obrigatoriamente.
                </Text>

                {userData.finalStepQuestions.map((item) => (
                  <div key={item.uid} className="item-row">
                    <Input placeholder="ID" value={item.id} style={{ width: '120px' }} onChange={(e) => {
                      const next = userData.finalStepQuestions.map(f => f.uid === item.uid ? { ...f, id: e.target.value } : f);
                      setUserData({ ...userData, finalStepQuestions: next });
                    }} />
                    <Input placeholder="Prompt da Pergunta" value={item.prompt} style={{ flex: 1 }} onChange={(e) => {
                      const next = userData.finalStepQuestions.map(f => f.uid === item.uid ? { ...f, prompt: e.target.value } : f);
                      setUserData({ ...userData, finalStepQuestions: next });
                    }} />
                    <Select placeholder="Validador" value={item.validator} style={{ width: '140px' }} onChange={(val) => {
                      const next = userData.finalStepQuestions.map(f => f.uid === item.uid ? { ...f, validator: val } : f);
                      setUserData({ ...userData, finalStepQuestions: next });
                    }} o options={[
                      { label: "Nome", value: "name" },
                      { label: "Data", value: "date" },
                      { label: "Empresa", value: "name" },
                      { label: "Email", value: "email" },
                      { label: "CPF", value: "cpf" },
                      { label: "Telefone", value: "phone" },
                    ]} />
                    <Button danger icon={<DeleteOutlined />} onClick={() => {
                      const next = userData.finalStepQuestions.filter(f => f.uid !== item.uid);
                      setUserData({ ...userData, finalStepQuestions: next });
                    }} />
                  </div>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => setUserData({ ...userData, finalStepQuestions: [...userData.finalStepQuestions, { uid: makeUid(), id: "", prompt: "", validator: "" }] })}>
                  Adicionar Campo ao Questionário
                </Button>
              </DynamicSection>


              <DynamicSection style={{ marginTop: '24px' }}>
                <Title level={4}><SecurityScanOutlined /> Regras de Condições</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Categorias e condições lógicas para o comportamento do bot.
                </Text>

                {userData.rulesConditions.map((item) => (
                  <div key={item.uid} className="item-row">
                    <Input placeholder="Categoria" value={item.categoria} style={{ width: '180px' }} onChange={(e) => {
                      const next = userData.rulesConditions.map(r => r.uid === item.uid ? { ...r, categoria: e.target.value } : r);
                      setUserData({ ...userData, rulesConditions: next });
                    }} />
                    <Input placeholder="Condição Lógica" value={item.condicao} style={{ flex: 1 }} onChange={(e) => {
                      const next = userData.rulesConditions.map(r => r.uid === item.uid ? { ...r, condicao: e.target.value } : r);
                      setUserData({ ...userData, rulesConditions: next });
                    }} />
                    <Button danger icon={<DeleteOutlined />} onClick={() => {
                      const next = userData.rulesConditions.filter(r => r.uid !== item.uid);
                      setUserData({ ...userData, rulesConditions: next });
                    }} />
                  </div>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => setUserData({ ...userData, rulesConditions: [...userData.rulesConditions, { uid: makeUid(), categoria: "", condicao: "" }] })}>
                  Adicionar Nova Regra
                </Button>
              </DynamicSection>

              <div style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                <Button onClick={() => navigate(-1)} size="large">Cancelar</Button>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={loading} size="large"
                  style={{ background: '#FF8F40', border: 'none', padding: '0 40px', borderRadius: '8px' }}>
                  Salvar Alterações
                </Button>z
              </div>
            </Form>
          </StyledForm>
        )}
      </ProfileCard>
    </PageWrapper>
  );
};

export default UserProfile;